import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../api/axiosConfig';
import {
  DocumentDuplicateIcon,
  UsersIcon,
  ShoppingCartIcon,
  ClipboardDocumentListIcon,
  // CurrencyDollarIcon, // Removed unused import
  ExclamationCircleIcon
} from '@heroicons/react/24/outline';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  Legend,
  PieChart,
  Pie,
  Cell
} from 'recharts';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import PageHeader from '../../components/common/PageHeader';

interface StatCardProps {
  title: string;
  value: string | number;
  icon: React.ReactNode;
  to: string;
  color: string;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, icon, to, color }) => {
  return (
    <Link to={to} className="block">
      <div className={`bg-white rounded-lg shadow-md p-6 transition-all duration-200 border-l-4 ${color} hover:shadow-lg`}>
        <div className="flex justify-between items-center">
          <div>
            <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
            <p className="text-2xl font-bold">{value}</p>
          </div>
          <div className="text-gray-400">
            {icon}
          </div>
        </div>
      </div>
    </Link>
  );
};

const Dashboard: React.FC = () => {
  const { user, can } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    structureCount: 0,
    clientCount: 0,
    activeOrderCount: 0,
    pendingWorkOrderCount: 0,
    totalRevenue: 0,
    pendingPayments: 0,
    availableStructures: 0
  });

  const [revenueData, setRevenueData] = useState<any[]>([]);
  const [alerts, setAlerts] = useState<any[]>([]);
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch summary statistics
        const statsResponse = await axios.get('/reports/dashboard-stats');

        // Fetch revenue chart data
        const revenueResponse = await axios.get('/reports/revenue-chart');

        // Fetch alerts
        const alertsResponse = await axios.get('/reports/alerts');

        // Fetch recent activity
        const activityResponse = await axios.get('/reports/recent-activity');

        // Update state with fetched data
        setStats(statsResponse.data);
        setRevenueData(revenueResponse.data);
        setAlerts(alertsResponse.data);
        setRecentActivity(activityResponse.data);
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
        setError('Не удалось загрузить данные для панели управления.');
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('ru-KZ', {
      style: 'currency',
      currency: 'KZT',
      maximumFractionDigits: 0
    }).format(value);
  };

  // Calculate data for Pie Chart
  const occupiedStructures = stats.structureCount - stats.availableStructures;
  const totalStructures = stats.structureCount;
  const pieData = [
    { name: 'Занято', value: occupiedStructures >= 0 ? occupiedStructures : 0 },
    { name: 'Свободно', value: stats.availableStructures >= 0 ? stats.availableStructures : 0 },
  ].filter(entry => entry.value > 0); // Avoid slices with 0 value if total is 0

  const COLORS = ['#ef4444', '#10b981']; // Red: Occupied, Green: Available

  return (
    <div>
      <PageHeader
        title="Панель управления"
        subtitle={`Добро пожаловать, ${user?.name || user?.username}!`}
      />

      {error && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 rounded-md mb-6" role="alert">
          <p className="font-bold">Ошибка</p>
          <p>{error}</p>
        </div>
      )}

      {/* Summary Statistics */}
      {!error && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
            <StatCard
              title="Конструкции"
              value={stats.structureCount}
              icon={<DocumentDuplicateIcon className="h-8 w-8" />}
              to="/structures"
              color="border-blue-500"
            />
            <StatCard
              title="Клиенты"
              value={stats.clientCount}
              icon={<UsersIcon className="h-8 w-8" />}
              to="/clients"
              color="border-green-500"
            />
            <StatCard
              title="Активные заказы"
              value={stats.activeOrderCount}
              icon={<ShoppingCartIcon className="h-8 w-8" />}
              to="/orders"
              color="border-yellow-500"
            />
            <StatCard
              title="Ожидающие наряды"
              value={stats.pendingWorkOrderCount}
              icon={<ClipboardDocumentListIcon className="h-8 w-8" />}
              to="/work-orders"
              color="border-red-500"
            />
          </div>

          {/* Financial Overview */}
          {can('dashboard.financial') && ( // Check if user has permission to view revenue}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
              <div className="lg:col-span-2 bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Доход за последние 6 месяцев</h2>
                <div className="h-72">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart
                      data={revenueData}
                      margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" />
                      <YAxis tickFormatter={(value) => `${value / 1000}K`} />
                      <Tooltip formatter={(value) => [`${formatCurrency(Number(value))}`, 'Доход']} />
                      <Area type="step" dataKey="revenue" stroke="#1e40af" fill="#3b82f6" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-2">Финансовый обзор</h2>
                <div className="mb-8">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600">Общий доход</span>
                    <span className="font-medium">{formatCurrency(stats.totalRevenue)}</span>
                  </div>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-gray-600">Ожидающие платежи</span>
                    <span className="font-medium text-amber-600">{formatCurrency(stats.pendingPayments)}</span>
                  </div>
                  <Link to="/payments" className="text-primary text-sm hover:underline">
                    Управление платежами →
                  </Link>
                </div>

                <h2 className="text-lg font-semibold mb-2">Общая занятость конструкций</h2>
                {totalStructures > 0 && pieData.length > 0 ? (
                  <div className="h-52"> {/* Adjusted height for PieChart */}
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          // label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`} // Optional label on slice
                          outerRadius={70} // Adjusted radius
                          fill="#8884d8"
                          dataKey="value"
                          paddingAngle={pieData.length > 1 ? 1 : 0} // Add padding angle if more than one slice
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip formatter={(value, name) => [`${value} шт.`, name]} />
                        <Legend verticalAlign="bottom" height={36} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                ) : (
                  <p className="text-center text-gray-500 mt-8 h-44 flex items-center justify-center">Нет данных о конструкциях.</p>
                )}
                <div className="mt-4 text-right">
                  <Link to="/structures/availability" className="text-primary text-sm hover:underline">
                    Смотреть доступность →
                  </Link>
                </div>
              </div>
            </div>
          )}

          {/* Alerts and Recent Activity */}
          {can('dashboard.recent_activity') && ( // Check if user has permission to view revenue}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Alerts */}
              <div className="bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Оповещения</h2>
                {alerts.length === 0 ? (
                  <p className="text-gray-500">Нет активных оповещений</p>
                ) : (
                  <ul className="space-y-3">
                    {alerts.map((alert) => (
                      <li key={alert.id} className="flex items-start space-x-3 p-3 bg-red-50 rounded-md">
                        <ExclamationCircleIcon className="h-5 w-5 text-red-500 mt-0.5" />
                        <div>
                          <p className="text-gray-800">{alert.message}</p>
                          <Link to={alert.path} className="text-primary text-sm hover:underline">
                            Подробнее →
                          </Link>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              {/* Recent Activity */}
              <div className="bg-white p-4 rounded-lg shadow">
                <h2 className="text-lg font-semibold mb-4">Недавняя активность</h2>
                {recentActivity.length === 0 ? (
                  <p className="text-gray-500">Нет недавней активности</p>
                ) : (
                  <ul className="space-y-3">
                    {recentActivity.map((activity) => {
                      const date = new Date(activity.date);
                      const formattedDate = new Intl.DateTimeFormat('ru-RU', {
                        day: 'numeric',
                        month: 'short'
                      }).format(date);

                      return (
                        <li key={activity.id} className="flex items-start space-x-3">
                          <div className="flex-shrink-0 w-10 text-center">
                            <span className="text-sm text-gray-500">{formattedDate}</span>
                          </div>
                          <div className="flex-1">
                            <p className="text-gray-800">{activity.message}</p>
                            <Link to={activity.path} className="text-primary text-sm hover:underline">
                              Просмотр →
                            </Link>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Dashboard;
